import React, { useState } from 'react';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import Pagination from './Pagination';
import Filters from './Filters';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BsPlus } from 'react-icons/bs';
import { Tooltip, TooltipProvider } from 'react-tooltip';

const Table = ({
  headers = [],
  rows = [],
  actions = [],
  actionsDisplayMode: initialActionsDisplayMode = 'inline', // 'inline' or 'dropdown'
  fixedActionMode = false, // When true, action display mode cannot be toggled
  className = '',
  emptyMessage = 'No data available',
  headerBgColor = 'bg-gray-50', // Default color, can be overridden
  onRowClick,
  title = '',
  itemsPerPage: initialItemsPerPage = 10,
  searchPlaceholder = 'Search...',
  onAdd,
  addButtonText = 'Add New',
  exportButtonText = 'Export',
  addButtonClassName = '',
  filterOptions = [], // New prop for filter options
  statusConfig = {}, // Add statusConfig prop
  showDateFilter = false, // Add date filter props
  onDateChange,
  dateFilterProps = {},
  showSearch = true, // Add showSearch prop with default true
  showClearFilter = true, // Add showClearFilter prop with default true
  showTotalCount = true, // Add showTotalCount prop with default true
  grid = false, // Add new grid prop with default false
}) => {
  // Check if there's data to display
  const hasData = rows && rows.length > 0;

  // Ensure headers is an array
  const tableHeaders = Array.isArray(headers) ? headers : [];

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state
  const [activeFilters, setActiveFilters] = useState([]);

  // Action display mode state
  const [actionsDisplayMode, setActionsDisplayMode] = useState(initialActionsDisplayMode);

  // Sort function
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  // Search function
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Reset function
  const handleReset = () => {
    setSearchQuery('');
    setCurrentPage(1);
    setActiveFilters([]); // Clear active filters
  };

  // Handle export
  const handleExport = (format) => {
    // Get visible columns (excluding actions)
    const visibleHeaders = headers.filter(h => h.key !== 'actions');

    switch (format) {
      case 'csv':
        exportToCSV(visibleHeaders);
        break;
      case 'excel':
        exportToExcel(visibleHeaders);
        break;
      case 'pdf':
        exportToPDF(visibleHeaders);
        break;
      default:
        exportToCSV(visibleHeaders);
    }
  };

  const exportToCSV = (visibleHeaders) => {
    try {
      const csvContent = [
        // Headers
        visibleHeaders.map(h => `"${h.label}"`).join(','),
        // Rows
        ...filteredAndSortedRows.map(row =>
          visibleHeaders.map(h => {
            const value = row[h.key];
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      downloadFile(csvContent, 'text/csv', 'csv');
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    }
  };

  const exportToExcel = (visibleHeaders) => {
    try {
      const excelContent = [
        // Headers
        visibleHeaders.map(h => h.label).join('\t'),
        // Rows
        ...filteredAndSortedRows.map(row =>
          visibleHeaders.map(h => row[h.key]).join('\t')
        )
      ].join('\n');

      downloadFile(excelContent, 'application/vnd.ms-excel', 'xls');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const exportToPDF = (visibleHeaders) => {
    try {
      // Create new PDF document
      const doc = new jsPDF('p', 'mm', 'a4');

      // Add title
      doc.setFontSize(16);
      doc.text(title || 'Table Export', 14, 15);

      // Add date
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);

      // Prepare table data
      const tableData = filteredAndSortedRows.map(row =>
        visibleHeaders.map(h => {
          const value = row[h.key];
          return value !== undefined ? String(value) : '';
        })
      );

      // Add table using autoTable
      autoTable(doc, {
        head: [visibleHeaders.map(h => h.label)],
        body: tableData,
        startY: 30,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { top: 30 },
        theme: 'grid',
        didDrawPage: function (data) {
          // Add page number
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageCount}`,
            data.settings.margin.left,
            doc.internal.pageSize.height - 10
          );
        }
      });

      // Save the PDF
      doc.save(`${title || 'table'}_export.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const downloadFile = (content, mimeType, extension) => {
    try {
      const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'table'}_export.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Filter and sort the rows
  const filteredAndSortedRows = React.useMemo(() => {
    let filteredRows = rows;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredRows = filteredRows.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(query)
        )
      );
    }

    // Apply status filters
    if (activeFilters.length > 0) {
      filteredRows = filteredRows.filter(row =>
        activeFilters.includes(row.status)
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      return [...filteredRows].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Handle numeric values
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Handle string values
        const aString = String(aValue || '').toLowerCase();
        const bString = String(bValue || '').toLowerCase();

        if (sortConfig.direction === 'asc') {
          return aString.localeCompare(bString);
        }
        return bString.localeCompare(aString);
      });
    }

    return filteredRows;
  }, [rows, searchQuery, sortConfig, activeFilters]);

  // Get current page items
  const paginatedRows = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedRows.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedRows, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Toggle action display mode
  const handleToggleActionMode = () => {
    if (!fixedActionMode) {
      setActionsDisplayMode(prevMode => prevMode === 'inline' ? 'dropdown' : 'inline');
    }
  };

  // Check if we have search results
  const hasSearchResults = filteredAndSortedRows.length > 0;

  return (
    <div className="relative">
      {title && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 lg:text-2xl">
            {title} {showTotalCount && hasData && <span className="text-gray-500">({rows.length})</span>}
          </h2>
        </div>
      )}

      <Filters
        onSearch={handleSearch}
        onRefresh={handleReset}
        onAdd={onAdd}
        onExport={handleExport}
        placeholder={searchPlaceholder}
        addButtonText={addButtonText}
        exportButtonText={exportButtonText}
        addButtonClassName={addButtonClassName}
        exportOptions={[
          { label: 'CSV', value: 'csv' },
          { label: 'Excel', value: 'excel' },
          { label: 'PDF', value: 'pdf' }
        ]}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        activeFilters={activeFilters}
        showDateFilter={showDateFilter}
        onDateChange={onDateChange}
        dateFilterProps={dateFilterProps}
        showSearch={showSearch}
        showClearFilter={showClearFilter}
      />

      <div className="relative overflow-x-auto rounded-lg shadow-lg ">
        <table className={`  min-w-full divide-y divide-gray-200 ${className} ${grid ? 'border-collapse' : ''}`}>
          <TableHeader
            headers={tableHeaders}
            headerBgColor={headerBgColor}
            sortConfig={sortConfig}
            onSort={handleSort}
            actionsDisplayMode={actionsDisplayMode}
            onToggleActionMode={handleToggleActionMode}
            fixedActionMode={fixedActionMode}
            grid={grid}
          />
          <tbody className="bg-white divide-y divide-gray-200">
            {!hasData ? (
              <tr>
                <td colSpan={tableHeaders.length + 1} className={`px-6 py-12 text-center ${grid ? 'border-x border-gray-200' : ''}`}>
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    <p className="mb-2 text-lg font-medium">
                      {emptyMessage}
                    </p>
                    {onAdd && (
                      <button
                        onClick={onAdd}
                        className="inline-flex items-center px-4 py-2 mt-4 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <BsPlus className="w-5 h-5 mr-2" />
                        Add New Item
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : !hasSearchResults ? (
              <tr>
                <td colSpan={tableHeaders.length + 1} className={`px-6 py-8 text-center ${grid ? 'border-x border-gray-200' : ''}`}>
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    <p className="mb-2 text-lg font-medium">
                      No matching items found
                    </p>
                    <button
                      onClick={handleReset}
                      className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Reset Filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  row={row}
                  headers={tableHeaders}
                  actions={actions}
                  actionsDisplayMode={actionsDisplayMode}
                  onRowClick={onRowClick}
                  statusConfig={statusConfig}
                  grid={grid}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <Tooltip
        id="table-tooltip"
        className="z-50 max-w-xs !text-xs !bg-gray-800 !text-white !opacity-100 !rounded-lg !p-2 !shadow-md"
        place="top"
        delayShow={300}
        noArrow={false}
        style={{
          backgroundColor: '#1f2937',
          borderRadius: '0.375rem', // Smaller border radius
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Lighter shadow for a softer effect
          padding: '0.5rem', // Reduced padding for a more compact tooltip
          zIndex: 50,
        }}
        arrowColor="#1f2937"
      />

      {hasSearchResults && (
        <Pagination
          currentPage={currentPage}
          totalItems={filteredAndSortedRows.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </div>
  );
};

export default Table;
